import React, { Component } from "react"
import PropTypes from "prop-types"
import { CustomPicker } from "react-color"
import { Hue, Saturation, Swatch } from "react-color/lib/components/common"
import { OldIconButton } from "@kit/design/old-button"
import { ColorPickerInputGroup } from "./color-picker-input-group"
import "./color-picker.css"
import { ColorsUpdaterToolbar } from "@kit/design/color-picker"
import { useSavedColors } from "hooks/use-saved-colors"

// There is an issue with the `colorpicker` ref returning a value from
// `getBoundingClientRect` that is further left than expected. The 50
// in the function below is a buffer until this gets figured out.
const offRightEdgeOfScreen = element => {
  const rect = element.getBoundingClientRect()
  return window.innerWidth - (rect.x + rect.width) < 50
}

// react-color represents full transparency as the literal string
// "transparent" rather than a hex code — leave it as-is; only saved-color
// hex values need the uppercase normalization saved colors are stored in.
const normalizeHex = hex => (hex === "transparent" ? hex : hex?.toUpperCase())

const byPlacement = placement => {
  switch (true) {
    case placement === "top": {
      return { top: "auto", bottom: 225 }
    }

    default: {
      return { top: 0, bottom: "auto" }
    }
  }
}

class BaseColorPicker extends Component {
  static propTypes = {
    active: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]),
    hex: PropTypes.string,
    hsl: PropTypes.object,
    onChange: PropTypes.func,
    onClose: PropTypes.func,
    swatches: PropTypes.arrayOf(PropTypes.string),
    placement: PropTypes.oneOf(["top", "bottom"]),
    savedColors: PropTypes.object
  }

  static defaultProps = {
    swatches: [],
    placement: "bottom"
  }

  constructor(props) {
    super(props)
    this.swatches = props.swatches.slice(0, 6)
    this.colorpicker = React.createRef()

    this.state = {
      position: { left: 0, ...byPlacement(this.props.placement) }
    }
  }

  componentDidMount() {
    document.addEventListener("mousedown", this.handleDocumentClick)
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.active && this.props.active && this.colorpicker.current) {
      // Calculate position only once when becoming active to avoid oscillation
      const useRightAlign = offRightEdgeOfScreen(this.colorpicker.current)
      this.setState({
        position: useRightAlign
          ? { right: 0, ...byPlacement(this.props.placement) }
          : { left: 0, ...byPlacement(this.props.placement) }
      })
    } else if (prevProps.active && !this.props.active) {
      // Reset to default position when closing so next open measures from consistent baseline
      this.setState({
        position: { left: 0, ...byPlacement(this.props.placement) }
      })
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleDocumentClick)
  }

  handleChange = color => {
    this.props.onChange(color)
  }

  handleClose = () => {
    this.props.onClose && this.props.onClose()
  }

  handleDocumentClick = event => {
    if (!this.colorpicker.current) return
    if (!this.colorpicker.current.contains(event.target)) {
      this.handleClose()
    }
  }

  render() {
    const active = this.props.active
    const order = (placement, reverse = false) => {
      const isTop = reverse ? placement !== "top" : placement === "top"
      return isTop ? "order-last border-t" : ""
    }

    return (
      <div
        className="core-color-picker"
        ref={this.colorpicker}
        style={this.state.position}
      >
        {active ? (
          <div
            data-active={active}
            className="core-color-picker__popup absolute hidden shadow-lg bg-white rounded flex-col"
          >
            <div
              className={"core-color-picker__group flex w-full p-2 gap-1 border-gray-100 ".concat(
                order(this.props.placement)
              )}
            >
              <div className="core-color-picker__current-swatch grow-0 shrink-0 border rounded border-gray-100 w-12 overflow-hidden">
                <Swatch color={this.props.hex} />
              </div>
              <ColorPickerInputGroup
                value={this.props.hex}
                onChange={this.handleChange}
              />
              <OldIconButton
                label="Close color picker"
                onClick={this.handleClose}
                className="text-base core-color-picker__close"
                icon="close"
              />
            </div>
            <div
              className={"core-color-picker__group flex w-full p-2 gap-1 border-gray-100 ".concat(
                order(this.props.placement, true)
              )}
            >
              <div className="core-color-picker__saturation">
                <Saturation {...this.props} onChange={this.handleChange} />
              </div>
              <div className="core-color-picker__hue">
                <Hue
                  direction="vertical"
                  hsl={this.props.hsl}
                  onChange={this.handleChange}
                />
              </div>
            </div>
            {this.swatches.length ? (
              <div className="core-color-picker__group">
                <div className="core-color-picker__swatches flex flex-auto">
                  {this.swatches.map((color, index) => (
                    <div
                      key={index}
                      className="flex-auto rounded h-6 overflow-hidden"
                    >
                      <Swatch color={color} onClick={this.handleChange} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              ""
            )}
            <div className="grid order-last">
              {this.props.children && (
                <div className="core-color-picker__group mb-2">
                  {this.props.children}
                </div>
              )}
              <div className="core-color-picker__group p-2 order-last">
                <ColorsUpdaterToolbar
                  {...this.props.savedColors}
                  handleColorSelect={this.handleChange}
                  currentColor={normalizeHex(this.props.hex)}
                  className="-mx-2"
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    )
  }
}

function ColorPickerWithSavedColors(props) {
  const savedColors = useSavedColors()
  return <BaseColorPicker {...props} savedColors={savedColors} />
}

export const ColorPicker = CustomPicker(ColorPickerWithSavedColors)
